import { useEffect, useState } from "react";
import type { AssistantActivityPauseInterval } from "../../types";

function mergePauseWindows(windows: Array<{ startedAt: number; endedAt: number }>) {
  const sorted = [...windows].sort((left, right) => left.startedAt - right.startedAt);
  const merged: Array<{ startedAt: number; endedAt: number }> = [];
  for (const window of sorted) {
    const previous = merged[merged.length - 1];
    if (previous && window.startedAt <= previous.endedAt) {
      previous.endedAt = Math.max(previous.endedAt, window.endedAt);
      continue;
    }
    merged.push({ ...window });
  }
  return merged;
}

export function computeActiveDurationSnapshot(args: {
  startedAt: number;
  now: number;
  pauseIntervals?: AssistantActivityPauseInterval[];
}) {
  const wallDurationMs = Math.max(0, args.now - args.startedAt);
  const pauseWindows = (args.pauseIntervals ?? [])
    .map((interval) => {
      const startedAt = Math.max(args.startedAt, interval.startedAt);
      const endedAt = Math.min(args.now, interval.endedAt ?? args.now);
      if (endedAt <= startedAt) {
        return null;
      }
      return { startedAt, endedAt };
    })
    .filter((window): window is { startedAt: number; endedAt: number } => Boolean(window));
  const pausedDurationMs = mergePauseWindows(pauseWindows).reduce(
    (total, window) => total + Math.max(0, window.endedAt - window.startedAt),
    0
  );
  const isPaused = (args.pauseIntervals ?? []).some(
    (interval) =>
      interval.startedAt <= args.now && (interval.endedAt === null || interval.endedAt > args.now)
  );
  return {
    durationMs: Math.max(0, wallDurationMs - pausedDurationMs),
    isPaused,
  };
}

export function useLiveDuration(
  startedAt: number,
  enabled: boolean,
  pauseIntervals: AssistantActivityPauseInterval[] = []
) {
  const [now, setNow] = useState(() => Date.now());
  const snapshot = computeActiveDurationSnapshot({
    startedAt,
    now,
    pauseIntervals,
  });
  const shouldTick =
    enabled &&
    !snapshot.isPaused &&
    !(
      typeof process !== "undefined" &&
      (process.env.NODE_ENV === "test" || "VITEST" in process.env)
    );

  useEffect(() => {
    void enabled;
    void startedAt;
    void pauseIntervals;
    setNow(Date.now());
  }, [enabled, startedAt, pauseIntervals]);

  useEffect(() => {
    if (!shouldTick) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [shouldTick]);

  return enabled ? snapshot : { durationMs: null, isPaused: false };
}
